/**
 * Robustly parses a date input (Date object, string, or timestamp) and converts it to a standard Date object.
 * If a date string is provided without timezone information, it is parsed using the fallbackTimezone.
 *
 * @param {Date|string|number} input - The date/time input to convert.
 * @param {'UTC'|'IST'} fallbackTimezone - The timezone to assume if the input string lacks timezone info.
 * @returns {Date} A JavaScript Date object.
 */
function parseToDate(input, fallbackTimezone = 'UTC') {
  if (input === null || input === undefined || input === '') {
    throw new Error('Input is null, undefined, or empty');
  }

  // 1. If it's already a Date object
  if (input instanceof Date) {
    if (isNaN(input.getTime())) {
      throw new Error('Invalid Date object');
    }
    return input;
  }

  // 2. If it's a number (timestamp)
  if (typeof input === 'number') {
    const date = new Date(input);
    if (isNaN(date.getTime())) {
      throw new Error('Invalid timestamp');
    }
    return date;
  }

  // 3. If it's a string
  if (typeof input === 'string') {
    let cleanStr = input.trim();

    // Check if the input is a numeric string (e.g. "1718788509000")
    if (/^\d+$/.test(cleanStr)) {
      const date = new Date(Number(cleanStr));
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Check if it already has timezone information:
    // - Contains 'Z' or 'z'
    // - Contains +HH:MM or -HH:MM (e.g., +05:30, -0400)
    // - Contains GMT or UTC
    const hasTimezone = /[Zz]|[+-]\d{2}:?\d{2}$|[+-]\d{4}$|GMT|UTC/i.test(cleanStr);

    if (hasTimezone) {
      const date = new Date(cleanStr);
      if (!isNaN(date.getTime())) {
        return date;
      }
      throw new Error(`Failed to parse date string with timezone: ${cleanStr}`);
    }

    // If it has no timezone info, normalize spacing and append the appropriate offset
    // Replacing spaces/T to construct a standard ISO-like string
    let isoCandidate = cleanStr.replace(/\s+/g, 'T');

    // Ensure it has time component. If it's just YYYY-MM-DD, append T00:00:00
    if (!isoCandidate.includes('T')) {
      isoCandidate += 'T00:00:00';
    } else {
      // If time component is incomplete (e.g., just HH:mm), make sure it has seconds
      const timeParts = isoCandidate.split('T')[1].split(':');
      if (timeParts.length === 1) {
        isoCandidate += ':00:00';
      } else if (timeParts.length === 2) {
        isoCandidate += ':00';
      }
    }

    let parsedDate;
   if (fallbackTimezone === 'IST') {
  // Attach IST offset explicitly
  parsedDate = new Date(`${isoCandidate}+05:30`);
}
     else {
      // Coordinated Universal Time (UTC) is UTC+00:00 / Z
      parsedDate = new Date(isoCandidate + 'Z');
    }

    if (!isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
    
    // Final fallback to native browser/Node parser
    parsedDate = new Date(cleanStr);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate;
    }

    throw new Error(`Unable to parse date string: ${cleanStr}`);
  }

  throw new Error('Unsupported date format');
}

/**
 * Formats a Date object to America/New_York (EST/EDT) in "YYYY-MM-DD HH:mm:ss" format.
 *
 * @param {Date} date - The date to format.
 * @returns {string} Formatted date string in EST/EDT.
 */
function formatToEST(date) {
  const options = {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  };

  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);

  const partMap = {};
  parts.forEach(p => {
    partMap[p.type] = p.value;
  });

  return `${partMap.year}-${partMap.month}-${partMap.day} ${partMap.hour}:${partMap.minute}:${partMap.second}`;
}

/**
 * Converts UTC time to EST/EDT (America/New_York timezone).
 * Supports Date objects, string dates, and numeric timestamps.
 * If the string lacks timezone info, it is assumed to be in UTC.
 *
 * @param {Date|string|number} input - UTC date/time input.
 * @returns {string} Converted date string in "YYYY-MM-DD HH:mm:ss" format.
 */
export function convertUTCtoEST(input) {
  try {
    const date = parseToDate(input, 'UTC');
    return formatToEST(date);
  } catch (error) {
    console.error('Error in convertUTCtoEST:', error.message);
    return 'Invalid Date';
  }
}

/**
 * Converts IST (Indian Standard Time) to EST/EDT (America/New_York timezone).
 * Supports Date objects, string dates, and numeric timestamps.
 * If the string lacks timezone info, it is assumed to be in IST (UTC+05:30).
 *
 * @param {Date|string|number} input - IST date/time input.
 * @returns {string} Converted date string in "YYYY-MM-DD HH:mm:ss" format.
 */
export function convertISTtoEST(input) {
  try {
    const date = parseToDate(input, 'IST');
    return formatToEST(date);
  } catch (error) {
    console.error('Error in convertISTtoEST:', error.message);
    return 'Invalid Date';
  }
}

/**
 * Formats a Date object to Asia/Kolkata (IST) in "YYYY-MM-DD HH:mm:ss" format.
 *
 * @param {Date} date - The date to format.
 * @returns {string} Formatted date string in IST.
 */
function formatToIST(date) {
  const options = {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  };

  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);

  const partMap = {};
  parts.forEach(p => {
    partMap[p.type] = p.value;
  });

  return `${partMap.year}-${partMap.month}-${partMap.day} ${partMap.hour}:${partMap.minute}:${partMap.second}`;
}

/**
 * Converts UTC time to IST (Asia/Kolkata timezone).
 * Supports Date objects, string dates, and numeric timestamps.
 * If the string lacks timezone info, it is assumed to be in UTC.
 *
 * @param {Date|string|number} input - UTC date/time input.
 * @returns {string} Converted date string in "YYYY-MM-DD HH:mm:ss" format.
 */
export function convertUTCtoIST(input) {
  try {
    const date = parseToDate(input, 'UTC');
    return formatToIST(date);
  } catch (error) {
    console.error('Error in convertUTCtoIST:', error.message);
    return 'Invalid Date';
  }
}

/**
 * Extracts just the date portion (no time) from a UTC date/time input, in "DD-MM-YYYY" format.
 * Does not apply any timezone shift — returns the date exactly as stored in UTC.
 *
 * @param {Date|string|number} input - UTC date/time input.
 * @returns {string} Date string in "DD-MM-YYYY" format.
 */
export function getUTCDateOnly(input) {
  try {
    const date = parseToDate(input, 'UTC');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error('Error in getUTCDateOnly:', error.message);
    return 'Invalid Date';
  }
}